import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ChiroWizard.module.css';
import type { ChiroPoint, ChiroTool, ChiroStep, ChiroDrawable } from '../types';
import { getHandler } from '../handlers';
import { autoDetectPoints, SUPPORTED_DETECTIONS } from '../detect';
import type { PluginContext } from '../../types';
import toolsData from '../data/tools.json';
import stepsData from '../data/steps.json';

const thumbUrls = import.meta.glob<string>('../assets/*.jpg', {
  eager: true,
  import: 'default',
  query: '?url',
});

function thumbUrl(toolId: number): string | undefined {
  return thumbUrls[`../assets/${toolId}.jpg`];
}

/**
 * The raw ct_reference_vc column ships with legacy Windows-style
 * indenting and a blank line between EVERY entry, which makes the panel
 * scroll for even short references. This normalizer:
 *   - trims each line
 *   - drops every blank line (no more inter-entry spacing)
 *   - keeps section labels (single capitalized word without ':') visually
 *     distinct by prefixing them with a bullet so CSS can style them.
 */
function tidyReference(raw: string): string {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const out: string[] = [];
  for (const line of lines) {
    const isSectionHeader = !line.includes(':') && /^[A-Za-z][A-Za-z '\-]{0,40}$/.test(line);
    out.push(isSectionHeader ? `▸ ${line}` : line);
  }
  return out.join('\n');
}

interface Props {
  toolId: number;
  ctx: PluginContext;
  onClose: () => void;
}

/**
 * Runs a chiropractic-tool measurement wizard.
 *
 * Two independent renderings:
 *  1. The floating panel (top-right, fixed) shows tool metadata, the
 *     reference thumbnail with numbered markers, the current step prompt,
 *     and Prev/Cancel/Done controls.
 *  2. An SVG overlay portalled into the active viewport DOM element.
 *     Captures pointer clicks, converts to world coords via
 *     viewport.canvasToWorld, and renders the handler's guide lines +
 *     the click points themselves. Redraws whenever Cornerstone renders
 *     the viewport so lines stay glued to anatomy under pan/zoom/scroll.
 */
export default function ChiroWizard({ toolId, ctx, onClose }: Props) {
  const tool = (toolsData as ChiroTool[]).find((t) => t.id === toolId);
  const steps = ((stepsData as unknown) as Record<string, ChiroStep[]>)[String(toolId)] || [];
  const handler = getHandler(toolId);

  const [points, setPoints] = useState<ChiroPoint[]>([]);
  // Bump this to force a re-render of the overlay when Cornerstone
  // re-renders the viewport (camera changed, image loaded, etc.).
  const [renderTick, setRenderTick] = useState(0);

  // Floating panel position. Draggable via the header, persisted in
  // sessionStorage so reopening a wizard puts the panel back where the
  // user last left it. Default: bottom-left corner of the visible area
  // (spine films render top-anchored, so bottom-left is dead space).
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ left: number; top: number }>(() => {
    try {
      const raw = sessionStorage.getItem('chiro.panelPos');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    // Estimate a reasonable default without the panel dimensions yet
    // (measured post-mount and clamped in the effect below).
    return { left: 20, top: Math.max(80, window.innerHeight - 620) };
  });
  useEffect(() => {
    try { sessionStorage.setItem('chiro.panelPos', JSON.stringify(panelPos)); } catch { /* ignore */ }
  }, [panelPos]);
  // Clamp to viewport bounds — runs on mount (once we know the panel's
  // real size) and on window resize.
  useEffect(() => {
    const clamp = () => {
      const el = panelRef.current;
      if (!el) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const maxL = Math.max(0, window.innerWidth  - w - 8);
      const maxT = Math.max(0, window.innerHeight - h - 8);
      setPanelPos((p) => ({
        left: Math.min(Math.max(0, p.left), maxL),
        top:  Math.min(Math.max(0, p.top),  maxT),
      }));
    };
    clamp();
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, []);

  // Pointer-drag on the header.
  const dragState = useRef<{ dx: number; dy: number } | null>(null);
  const onHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Ignore drags initiated on interactive children (close button).
    if ((e.target as HTMLElement).closest('button')) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragState.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragState.current;
    if (!s) return;
    const el = panelRef.current;
    if (!el) return;
    const maxL = Math.max(0, window.innerWidth  - el.offsetWidth  - 8);
    const maxT = Math.max(0, window.innerHeight - el.offsetHeight - 8);
    setPanelPos({
      left: Math.min(Math.max(0, e.clientX - s.dx), maxL),
      top:  Math.min(Math.max(0, e.clientY - s.dy), maxT),
    });
  };
  const onHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragState.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  const viewportEl = ctx.getActiveViewportElement();
  const viewport = ctx.getActiveViewport();

  // Wire viewport render events → overlay repaint. Falls back to a
  // camera-modified listener if the image-rendered one is missing.
  useEffect(() => {
    if (!viewportEl) return;
    const bump = () => setRenderTick((t) => t + 1);
    const events = [
      'CORNERSTONE_IMAGE_RENDERED',
      'CORNERSTONE_CAMERA_MODIFIED',
      'CORNERSTONE_STACK_NEW_IMAGE',
    ];
    for (const e of events) viewportEl.addEventListener(e, bump);
    window.addEventListener('resize', bump);
    return () => {
      for (const e of events) viewportEl.removeEventListener(e, bump);
      window.removeEventListener('resize', bump);
    };
  }, [viewportEl]);

  /** Translate a pointer's client coords → world (mm) coords via the
   *  active viewport. Returns null if the viewport can't be reached. */
  const clientToWorld = useCallback(
    (clientX: number, clientY: number): ChiroPoint | null => {
      if (!viewport) return null;
      const canvas = viewport.getCanvas?.() as HTMLCanvasElement | undefined;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const world = viewport.canvasToWorld([clientX - rect.left, clientY - rect.top]);
      return { worldX: world[0], worldY: world[1], worldZ: world[2] ?? 0 };
    },
    [viewport],
  );

  /** Index of the point currently being dragged, or null. Stored in a
   *  ref (not state) so pointermove doesn't trigger a re-render just to
   *  read it. Point updates below still go through setPoints so the
   *  render + measurement labels stay reactive. */
  const dragPointIdx = useRef<number | null>(null);

  // Stop Cornerstone's active tool (window/level, zoom, pan) from
  // seeing our clicks and drags. The SVG overlay is portalled INTO
  // the viewport DOM element, so pointer events bubble up unless we
  // call stopPropagation.
  const handleViewportPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!viewport || !handler) return;
      e.stopPropagation();
      // Clicked on an existing point → start drag instead of placing a new one.
      const hit = (e.target as Element).closest('[data-point-index]');
      if (hit) {
        dragPointIdx.current = Number(hit.getAttribute('data-point-index'));
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        e.preventDefault();
        return;
      }
      // Otherwise place a new point if we still have room in the sequence.
      if (points.length >= handler.expectedPointCount) return;
      const world = clientToWorld(e.clientX, e.clientY);
      if (world) setPoints((prev) => [...prev, world]);
    },
    [viewport, handler, points.length, clientToWorld],
  );

  const handleViewportPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const idx = dragPointIdx.current;
      if (idx === null) return;
      e.stopPropagation();
      e.preventDefault();
      const world = clientToWorld(e.clientX, e.clientY);
      if (!world) return;
      setPoints((prev) => prev.map((p, i) => (i === idx ? world : p)));
    },
    [clientToWorld],
  );

  const handleViewportPointerUp = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (dragPointIdx.current === null) return;
      e.stopPropagation();
      dragPointIdx.current = null;
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    },
    [],
  );

  // -- Auto-detect (dcx-detect microservice) --
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [detectNote, setDetectNote] = useState<string | null>(null);
  const canAutoDetect = SUPPORTED_DETECTIONS.has(toolId);
  const runAutoDetect = useCallback(async () => {
    if (!viewport) return;
    setDetecting(true);
    setDetectError(null);
    setDetectNote(null);
    try {
      const result = await autoDetectPoints(viewport, toolId);
      setPoints(result.points);
      if (result.fellBack) {
        setDetectNote(
          'Detector could not find clear end-plates in this image — placed default points instead. Drag them onto real landmarks.',
        );
      } else {
        setDetectNote(`Detected via ${result.method}. Drag any point to refine.`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[chiro] auto-detect failed:', msg);
      setDetectError(msg);
    } finally {
      setDetecting(false);
    }
  }, [viewport, toolId]);

  const undo = useCallback(() => {
    setPoints((prev) => prev.slice(0, -1));
  }, []);

  const restart = useCallback(() => {
    setPoints([]);
  }, []);

  // World → canvas coordinate transform for rendering the overlay.
  const worldToScreen = useCallback(
    (p: ChiroPoint): { x: number; y: number } | null => {
      if (!viewport) return null;
      try {
        const [x, y] = viewport.worldToCanvas([p.worldX, p.worldY, p.worldZ]);
        return { x, y };
      } catch {
        return null;
      }
    },
    // renderTick is intentional — re-derives on each camera-modified event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewport, renderTick],
  );

  const drawables: ChiroDrawable[] = useMemo(
    () => (handler ? handler.render(points) : []),
    [handler, points],
  );

  const result = useMemo(() => {
    if (!handler || points.length !== handler.expectedPointCount) return null;
    return handler.compute(points);
  }, [handler, points]);

  const currentStep = steps[points.length];
  const done = handler ? points.length >= handler.expectedPointCount : false;

  const overlaySize = useMemo(() => {
    if (!viewport) return { w: 0, h: 0 };
    const canvas = viewport.getCanvas?.() as HTMLCanvasElement | undefined;
    if (!canvas) return { w: 0, h: 0 };
    const rect = canvas.getBoundingClientRect();
    return { w: rect.width, h: rect.height };
  }, [viewport, renderTick]);

  if (!tool) {
    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <h2 className={styles.title}>Unknown tool</h2>
          <button className={styles.close} onClick={onClose}>×</button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Floating control panel — draggable via the header. */}
      <div
        ref={panelRef}
        className={styles.panel}
        style={{ left: panelPos.left, top: panelPos.top, right: 'auto' }}
      >
        <div
          className={styles.header}
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          onPointerCancel={onHeaderPointerUp}
        >
          <h2 className={styles.title}>
            {tool.name}
            {handler && (
              <span className={styles.stepInline}>
                {` — ${Math.min(points.length + 1, handler.expectedPointCount)}/${handler.expectedPointCount}`}
              </span>
            )}
          </h2>
          <div className={styles.headerActions}>
            {canAutoDetect && (
              <button
                className={styles.autoDetectBtn}
                onClick={runAutoDetect}
                disabled={detecting || !viewport}
                title="Detect landmarks automatically (server-side CV)"
              >
                {detecting ? 'Detecting…' : 'Auto-detect'}
              </button>
            )}
            <button className={styles.close} onClick={onClose} title="Close">×</button>
          </div>
        </div>

        {detectError && (
          <div className={styles.detectError}>
            Auto-detect failed: {detectError}. You can still click points manually.
          </div>
        )}

        {detectNote && !detectError && (
          <div className={styles.detectNote}>{detectNote}</div>
        )}

        <ThumbnailPanel toolId={toolId} steps={steps} currentIndex={points.length} />

        {!done && currentStep && (
          <div className={styles.prompt}>{currentStep.prompt}</div>
        )}

        {result && (
          <div className={styles.result}>{result.summary}</div>
        )}

        {tool.reference && tool.reference !== 'None' && tool.reference !== 'none' && (
          <div className={styles.reference}>{tidyReference(tool.reference)}</div>
        )}

        <div className={styles.footer}>
          <button className={styles.btn} onClick={undo} disabled={points.length === 0}>
            Undo
          </button>
          <button className={styles.btn} onClick={restart} disabled={points.length === 0}>
            Restart
          </button>
          <button className={styles.btn + ' ' + styles.primary} onClick={onClose}>
            {done ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>

      {/* Viewport click-capture + line rendering overlay, portalled into
          the active viewport DOM element. Guard for the no-active-viewport
          case (e.g. all viewports empty). */}
      {viewportEl && viewport && handler &&
        createPortal(
          <svg
            className={styles.viewportOverlay}
            width={overlaySize.w}
            height={overlaySize.h}
            viewBox={`0 0 ${overlaySize.w} ${overlaySize.h}`}
            onPointerDown={handleViewportPointerDown}
            onPointerMove={handleViewportPointerMove}
            onPointerUp={handleViewportPointerUp}
            onPointerCancel={handleViewportPointerUp}
          >
            {/* Guide lines / labels from the handler */}
            {drawables.map((d, i) => {
              if (d.kind === 'line') {
                const a = worldToScreen(d.a);
                const b = worldToScreen(d.b);
                if (!a || !b) return null;
                return (
                  <line
                    key={`d${i}`}
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={d.style?.color ?? '#ffd400'}
                    strokeWidth={2}
                    strokeDasharray={d.style?.dashed ? '6 4' : undefined}
                  />
                );
              }
              if (d.kind === 'label') {
                const p = worldToScreen(d.at);
                if (!p) return null;
                return (
                  <text
                    key={`d${i}`}
                    x={p.x + 8}
                    y={p.y - 8}
                    fill={d.style?.color ?? '#ffd400'}
                    fontSize={d.style?.fontSize ?? 14}
                    fontWeight={600}
                    style={{ paintOrder: 'stroke', stroke: '#000', strokeWidth: 3 }}
                  >
                    {d.text}
                  </text>
                );
              }
              return null;
            })}
            {/* Placed click points. Each point is wrapped in a <g> with
                a data-point-index so pointerdown can start a drag on it
                instead of adding a new point. The outer transparent
                circle is the drag hit-target (larger than the visible
                dot for easy grabbing); inner visible marks are made
                pointer-transparent so they never intercept. */}
            {points.map((p, i) => {
              const s = worldToScreen(p);
              if (!s) return null;
              return (
                <g key={`p${i}`} data-point-index={i} style={{ cursor: 'grab' }}>
                  <circle
                    cx={s.x} cy={s.y} r={14}
                    fill="transparent"
                    stroke="none"
                  />
                  <circle
                    cx={s.x} cy={s.y} r={5}
                    fill="#ffd400" stroke="#000" strokeWidth={1}
                    style={{ pointerEvents: 'none' }}
                  />
                  <text
                    x={s.x + 8}
                    y={s.y + 4}
                    fill="#ffd400"
                    fontSize={11}
                    fontWeight={700}
                    style={{ pointerEvents: 'none', paintOrder: 'stroke', stroke: '#000', strokeWidth: 3 }}
                  >
                    {i + 1}
                  </text>
                </g>
              );
            })}
          </svg>,
          viewportEl,
        )
      }
    </>
  );
}

/**
 * Reference thumbnail with numbered markers overlaid at (markerX, markerY).
 * The markers are positioned in the thumbnail's native pixel space and
 * scaled proportionally by the img's rendered size, so we compute a scale
 * factor after the image loads.
 */
function ThumbnailPanel({
  toolId,
  steps,
  currentIndex,
}: {
  toolId: number;
  steps: ChiroStep[];
  currentIndex: number;
}) {
  const url = thumbUrl(toolId);
  const imgRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState<{ sx: number; sy: number; ox: number; oy: number }>({
    sx: 1, sy: 1, ox: 0, oy: 0,
  });

  const recompute = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return;
    const sx = img.width / img.naturalWidth;
    const sy = img.height / img.naturalHeight;
    // Offset from the container's origin to the image's top-left (image is
    // centered by flex).
    const parent = img.parentElement?.getBoundingClientRect();
    const rect = img.getBoundingClientRect();
    setScale({
      sx, sy,
      ox: parent ? rect.left - parent.left : 0,
      oy: parent ? rect.top - parent.top : 0,
    });
  }, []);

  useEffect(() => {
    recompute();
    const ro = new ResizeObserver(recompute);
    if (imgRef.current) ro.observe(imgRef.current);
    return () => ro.disconnect();
  }, [recompute]);

  if (!url) return null;

  return (
    <div className={styles.thumbWrap}>
      <img ref={imgRef} className={styles.thumb} src={url} onLoad={recompute} alt="" />
      {steps.map((s, i) => {
        const done = i < currentIndex;
        const current = i === currentIndex;
        return (
          <div
            key={s.order}
            className={`${styles.marker} ${current ? styles.markerCurrent : ''} ${done ? styles.markerDone : ''}`}
            style={{
              left: scale.ox + s.markerX * scale.sx,
              top:  scale.oy + s.markerY * scale.sy,
            }}
            title={s.prompt}
          >
            {i + 1}
          </div>
        );
      })}
    </div>
  );
}
